/**
 * Composing the agent-facing message for a stuck/frozen preview.
 *
 * When PreviewPanel gives up showing the running app it hands the Workspace a
 * {@link PreviewStuckReport} (failure CLASS + route). These helpers turn that into the
 * request Synthase receives. The wording states ONLY the observed symptom + the route and
 * asks the agent to diagnose it itself — no prefix tag, no assumed-cause checklist, no
 * stack assumptions (prescribing a cause we can't know misleads the agent). Kept pure +
 * separate from Workspace.tsx so the wording is unit-testable in isolation.
 *
 * @module
 */

import type { PreviewStuckReport } from '@molecule/app-ide-react'

/**
 * Reduce a preview location to the route path shown to the agent — strip the origin (and
 * the iframe cache-buster query) so `http://localhost:5173/instructor?t=9` reads as
 * `/instructor`. Returns '' for a missing/unparseable url so the caller can omit the
 * "on `<route>`" clause entirely rather than print a bare or broken path.
 *
 * @param url - The preview's current location, if known.
 * @returns The path (with any meaningful search, sans cache-buster), or '' when unavailable.
 */
export function formatStuckRoute(url?: string): string {
  if (!url) return ''
  try {
    const u = new URL(url)
    u.searchParams.delete('t') // the iframe cache-buster (withCacheBuster) — noise to the agent
    const search = u.searchParams.toString()
    return `${u.pathname}${search ? `?${search}` : ''}`
  } catch (_error) {
    // Not an absolute URL (or already a bare path) — keep it only if it looks like a
    // path, else drop it. Either way we never throw out of a report.
    return url.startsWith('/') ? url : ''
  }
}

/**
 * Compose the message dispatched to Synthase for a stuck/frozen preview. The message
 * states only what the IDE actually OBSERVED (the symptom + the route) and tells the
 * agent to diagnose it itself — deliberately no "[Preview …]" prefix, no list of assumed
 * causes, and no stack assumptions: prescribing causes (an import error, a React render
 * loop, a missing route) when we don't know the project misleads the agent. The one fact
 * we can state is the observed difference between the two classes: a `frozen` preview
 * RENDERED and then went unresponsive (its periodic liveness signal stopped → the main
 * thread is blocked); the others NEVER displayed the app at all.
 *
 * @param report - The failure report from PreviewPanel (undefined → generic "never displayed").
 * @returns The full message to send as a fix-with-AI request.
 */
export function composePreviewStuckMessage(report?: PreviewStuckReport): string {
  const route = formatStuckRoute(report?.url)
  if (report?.reason === 'frozen') {
    const at = route ? ` at \`${route}\`` : ''
    return `The live preview loaded${at} and then became completely unresponsive — it stopped responding entirely, which means its main thread is blocked. Reproduce this, work out what on this route blocks the thread, and fix it. Don't assume the cause — investigate.`
  }
  const to = route ? ` to \`${route}\`` : ''
  return `The live preview navigated${to} but never displayed the app. Reproduce this and work out why it doesn't render there — don't assume the cause. Check the dev server output and the browser console for this route and trace the code that runs for it.`
}
